import { Box } from "@material-ui/core";
import React, { FC, useState, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState } from "recoil";

import { JobCategory } from "@/api/generated";
import { JOB_INDUSTRIES, OCCUPATIONS } from "@/api/options/account";
import { CustomForm } from "@/components/Form";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { PickerField } from "@/components/selection/PickerField";
import { RegistrationFlowLayout as RegistrationTemplate, RegistrationActions } from "@/layouts/RegistrationFlowLayout";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

interface RegisterJobPageProps {
  totalSteps: number;
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

const period_ranges = ["6ヶ月未満", "6ヶ月以上1年未満", "1年以上3年未満", "3年以上"];

const RegisterJobPage: FC<RegisterJobPageProps> = ({ totalSteps, currentStep, onNext, onBack, validate }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const watchValues = useWatch({
    name: ["job_category"],
    control: control,
  });
  // 現在のお住まい入力内容により表示フォームを変更するために必要な判定用変数
  const [inputJobCategory, setInputJobCategory] = useState<JobCategory>(JobCategory.入力なし);
  const [formData] = useRecoilState(UserRegistrationsAccoutFormState);

  const handleNext = async () => {
    const isValid = await validate();
    if (isValid) {
      onNext();
    }
  };

  useEffect(() => {
    // スクロールを最上部へ
    window.scrollTo({ top: 0, behavior: "smooth" });
    // formDataが存在する場合に初期値を設定
    control.setValue("job_category", formData.job_category);
    control.setValue("job_continuous_period", formData.job_continuous_period);
    control.setValue("annual_income", formData.annual_income);
  }, []);

  // 職業入力内容により業界フォームの表示・非表示を変更するためのuseEffect
  useEffect(() => {
    setInputJobCategory(watchValues.job_category);
  }, [watchValues.job_category]);

  // inputJobCategory が変更された際に、job_industry の初期値を設定する
  useEffect(() => {
    control.setValue("job_industry", formData.job_industry);
  }, [inputJobCategory]);

  const occupationOptions = OCCUPATIONS.map((occupation) => ({ value: occupation, label: occupation }));
  const jobCategoryOptions = JOB_INDUSTRIES.map((jobCategory) => ({ value: jobCategory, label: jobCategory }));
  const periodRangeOptions = period_ranges.map((periodRange) => ({ value: periodRange, label: periodRange }));

  return (
    <RegistrationTemplate
      phaseTags={<RegistrationPhases completed />}
      title="現在のお仕事について教えてください"
      totalSteps={totalSteps}
      currentStep={currentStep}
      description="入力内容を元に最適な情報を提供します"
    >
      <Box mt={4}>
        <Controller
          name="job_category"
          control={control}
          render={({ ...field }) => (
            <PickerField
              {...field}
              label="ご職業"
              error={errors.job_category?.message}
              onChange={field.onChange}
              options={occupationOptions}
            />
          )}
        />
        {/* 業種条件付きで表示 */}
        {[JobCategory.会社員上場, JobCategory.会社員非上場, JobCategory.会社員外資系企業].includes(
          inputJobCategory
        ) && (
          <Controller
            name="job_industry"
            control={control}
            render={({ ...field }) => (
              <PickerField
                {...field}
                label="業種"
                error={errors.job_industry?.message}
                onChange={field.onChange}
                options={jobCategoryOptions}
                searchable
              />
            )}
          />
        )}

        <Controller
          name="job_continuous_period"
          control={control}
          render={({ ...field }) => (
            <PickerField
              {...field}
              label="勤続年数"
              error={errors.job_continuous_period?.message}
              onChange={field.onChange}
              options={periodRangeOptions}
            />
          )}
        />

        <Controller
          name="annual_income"
          control={control}
          render={({ ...field }) => (
            <CustomForm
              {...field}
              label="年収"
              error={!!errors.annual_income}
              errorText={errors.annual_income?.message}
              onChange={field.onChange}
              placeHolder="入力してください"
              unitSuffix="万円"
              formatNumeric
            />
          )}
        />
      </Box>
      <RegistrationActions onNext={handleNext} onBack={onBack} />
    </RegistrationTemplate>
  );
};

export default RegisterJobPage;
